import { isAxiosError } from "axios";
import { type FC, useState } from "react";

import { serverCreateLinkedSections } from "api/api-server-publishing";
import { CourseMenu } from "components/menus/CourseMenu";
import { SectionMenu } from "components/menus/SectionMenu";
import { TermMenu } from "components/menus/TermMenu";
import { SectionKind } from "components/server-types";
import { t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { Button } from "mds/components/Button";
import { RadioButtonCard } from "mds/components/RadioButtonCard";
import { TextField } from "mds/components/TextField";
import { BookSectionIcon, BookSectionLinkedIcon, BookSectionTemplateIcon } from "mds/icons";
import { storeApi } from "store/index";
import { toastLocalizedOperationCatcher, toastSuccessMessage } from "utils/alerts";
import { isTemplateSection } from "utils/publishing";
import { isCCMEnabled } from "utils/waffle-flags";

const SECTION_KIND_OPTIONS = [
  {
    kind: SectionKind.UNLINKED,
    icon: <BookSectionIcon size="m" />,
    labelKey: "home.add_section_modal.section_type_default_label",
    descriptionKey: "home.add_section_modal.section_type_default_description",
  },
  {
    kind: SectionKind.TEMPLATE,
    icon: <BookSectionTemplateIcon size="m" />,
    labelKey: "home.add_section_modal.section_type_template_label",
    descriptionKey: "home.add_section_modal.section_type_template_description",
  },
  {
    kind: SectionKind.LINKED,
    icon: <BookSectionLinkedIcon size="m" />,
    labelKey: "home.add_section_modal.section_type_linked_label",
    descriptionKey: "home.add_section_modal.section_type_linked_description",
  },
] as const;

type AddSectionModalProps = {
  eventName?: string;
  eventProperties?: Record<string, unknown>;
  onClose: () => void;
  defaultCourseId?: string | null;
  defaultTermId?: string | null;
  defaultTemplateId?: string | null;
};

export const AddSectionModal: FC<AddSectionModalProps> = ({
  eventName,
  eventProperties,
  onClose,
  defaultCourseId,
  defaultTermId,
  defaultTemplateId,
}) => {
  const ccmEnabled = isCCMEnabled();
  const [title, setTitle] = useState("");
  const [selectedTermId, setSelectedTermId] = useState<string>(defaultTermId || "");
  const [selectedCourseId, setSelectedCourseId] = useState<string>(defaultCourseId || "");
  const defaultSectionKind = ccmEnabled
    ? defaultTemplateId
      ? SectionKind.LINKED
      : undefined
    : SectionKind.UNLINKED;
  const [sectionKind, setSectionKind] = useState<SectionKind | undefined>(defaultSectionKind);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    defaultTemplateId || null,
  );
  const [linkedSectionCount, setLinkedSectionCount] = useState(1);
  const [titleUnavailableError, setTitleUnavailableError] = useState(false);

  const isValid = () =>
    Boolean(
      title.trim() &&
        selectedCourseId &&
        selectedTermId &&
        sectionKind &&
        (sectionKind !== SectionKind.LINKED || selectedTemplateId),
    );

  const handleSubmit = () => {
    if (!isValid()) return;

    if (sectionKind === SectionKind.LINKED) {
      serverCreateLinkedSections(selectedTemplateId!, linkedSectionCount, title.trim())
        .then(() => {
          toastSuccessMessage(t("home.toasts.section_added"));
          onClose();
        })
        .catch(
          toastLocalizedOperationCatcher("create_failure", {
            item: t("glossary.sections"),
          }),
        );
    } else {
      setTitleUnavailableError(false);
      storeApi.sections
        .create({
          course_id: selectedCourseId,
          kind: sectionKind,
          term_id: selectedTermId || undefined,
          title: title.trim(),
        })
        .then(() => {
          toastSuccessMessage(t("home.toasts.section_added"));
          onClose();
        })
        .catch((error: unknown) => {
          if (!isAxiosError(error) || error.response?.status !== 400) {
            toastLocalizedOperationCatcher("create_failure", {
              item: t("glossary.sections"),
            })(error as Error);
            return;
          }
          const data = error.response?.data as { title?: string[] };
          const titleErrMsg = Array.isArray(data?.title) ? data.title[0] : data?.title;
          if (typeof titleErrMsg === "string" && titleErrMsg === "err_section_title_unavailable") {
            setTitleUnavailableError(true);
          } else {
            toastLocalizedOperationCatcher("create_failure", {
              item: t("glossary.sections"),
            })(error as Error);
          }
        });
    }
  };

  const handleTermChange = (termId: string) => {
    setSelectedTermId(termId);
  };

  const handleCourseChange = (courseId: string) => {
    setSelectedCourseId(courseId);
  };

  return (
    <div className="mt-8 flex flex-col gap-4">
      {titleUnavailableError && (
        <Banner kind="error">{t("home.add_section_modal.title_unavailable")}</Banner>
      )}
      <div className="flex gap-4">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <label className="flex flex-col gap-1">
            <div className="font-medium">{t("home.terms.select_term_label")}</div>

            <TermMenu
              defaultToAll={false}
              menuClassName="max-h-344"
              menuType="menu"
              selectedTermId={selectedTermId}
              onTermChange={handleTermChange}
            />
          </label>
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <label className="flex flex-col gap-1">
            <div className="font-medium">{t("home.courses.select_course_label")}</div>

            <CourseMenu
              defaultToAll={false}
              menuClassName="max-h-344"
              menuType="menu"
              selectedCourseId={selectedCourseId}
              adminAccessibleOnly
              onCourseChange={handleCourseChange}
            />
          </label>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label className="flex flex-col gap-1">
          <div className="font-medium">{t("home.add_section_modal.section_title_label")}</div>

          <TextField
            eventName={eventName}
            eventProperties={eventProperties}
            placeholder={t("home.placeholder.section_title")}
            value={title}
            isEditing
            onChange={(e) => {
              setTitle(String(e.target.value));
              setTitleUnavailableError(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && isValid()) handleSubmit();
            }}
          />
        </label>
      </div>

      {ccmEnabled && (
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1">
            <div className="font-medium">{t("home.add_section_modal.section_type_label")}</div>
            <div className="flex flex-col gap-2">
              {SECTION_KIND_OPTIONS.map(({ kind, icon, labelKey, descriptionKey }) => (
                <RadioButtonCard
                  checked={sectionKind === kind}
                  description={t(descriptionKey)}
                  icon={icon}
                  id={`section-kind-${kind}`}
                  key={kind}
                  label={t(labelKey)}
                  name="section-kind"
                  value={kind}
                  onChange={() => setSectionKind(kind)}
                >
                  {kind === SectionKind.LINKED && sectionKind === SectionKind.LINKED && (
                    <div className="flex gap-4">
                      <div className="flex min-w-0 flex-1 flex-col gap-1">
                        <label className="body-xs text-black-tint-40">
                          {t("home.add_section_modal.template_label")}
                        </label>

                        <SectionMenu
                          emptyOptionText={t("home.add_section_modal.select_template")}
                          filterBy={(section) =>
                            isTemplateSection(section) && section.course_id === selectedCourseId
                          }
                          menuClassName="max-h-344"
                          menuType="menu"
                          selectedSectionId={selectedTemplateId}
                          onSectionChange={setSelectedTemplateId}
                        />
                      </div>

                      <div className="flex min-w-0 flex-1 flex-col gap-1">
                        <label className="body-xs text-black-tint-40">
                          {t("home.add_section_modal.sections_label")}
                        </label>
                        <TextField
                          max={10}
                          min={1}
                          size="s"
                          type="number"
                          value={linkedSectionCount.toString()}
                          isEditing
                          onChange={(e) => {
                            const n = parseInt(e.target.value, 10);
                            if (!Number.isNaN(n) && n >= 1) setLinkedSectionCount(n);
                          }}
                        />
                      </div>
                    </div>
                  )}
                </RadioButtonCard>
              ))}
            </div>
          </label>
        </div>
      )}

      <div className="mt-4 flex w-full justify-end gap-2">
        <Button kind="white" size="s" title={t("common.cancel")} onClick={onClose}>
          {t("common.cancel")}
        </Button>

        <Button
          disabled={!isValid()}
          kind="blue"
          size="s"
          title={t("home.add_section_modal.create_section")}
          onClick={handleSubmit}
        >
          {t("home.add_section_modal.create_section")}
        </Button>
      </div>
    </div>
  );
};
